import { computed, Injectable, signal } from '@angular/core';
import { AppNotification } from '@interfaces/notification';
import { Observable, of, tap } from 'rxjs';
import { MOCK_NOTIFICATIONS } from './notifications.mock';

/**
 * Notifications for the global header bell.
 *
 * There is no notifications API yet (EN-480 ships the UI shell only), so
 * `fetchNotifications` is the single stubbed seam: when the backend lands,
 * swap its body for an `ApiService` URL + `HttpClient` call and nothing else
 * in the header has to change.
 *
 * The stub returns an empty list in every environment except when
 * `useMockNotifications` is turned on locally, so users never see fabricated
 * activity.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  /** Flip to `true` locally to eyeball the populated panel styling. */
  private readonly useMockNotifications = false;

  private readonly _notifications = signal<AppNotification[]>([]);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);

  public readonly notifications = this._notifications.asReadonly();
  public readonly loading = this._loading.asReadonly();
  public readonly error = this._error.asReadonly();

  public readonly unreadCount = computed(() => this._notifications().filter((n) => !n.read).length);
  public readonly hasUnread = computed(() => this.unreadCount() > 0);

  public load(): Observable<AppNotification[]> {
    this._loading.set(true);
    this._error.set(null);

    return this.fetchNotifications().pipe(
      tap({
        next: (notifications) => {
          this._notifications.set(notifications);
          this._loading.set(false);
        },
        error: (err) => {
          this._error.set(err?.message ?? 'unexpected_error');
          this._loading.set(false);
        },
      }),
    );
  }

  public markRead(id: string): void {
    this._notifications.update((notifications) =>
      notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
  }

  public markAllRead(): void {
    this._notifications.update((notifications) => notifications.map((n) => ({ ...n, read: true })));
  }

  public clear(): void {
    this._notifications.set([]);
  }

  /** The only place that will change once the notifications API exists. */
  private fetchNotifications(): Observable<AppNotification[]> {
    return of(this.useMockNotifications ? MOCK_NOTIFICATIONS : []);
  }
}
